import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Select, message } from 'antd';
import { RiHome2Line } from "react-icons/ri";
import { PiBagSimpleBold } from "react-icons/pi";
import { IoLocationOutline } from "react-icons/io5";
import axios from 'axios';
import { API_BASE_URL } from '../../services/apiConfig';
import { buildAddressPayload, getAddressId } from '../../services/addressApi';
import "../ModalCom/AddressModal.css";

const { Option } = Select;

const COUNTRY_DIAL_CODES = [
  { country: 'India', code: 'IN', dial: '+91' },
  { country: 'United States', code: 'US', dial: '+1' },
  { country: 'United Kingdom', code: 'GB', dial: '+44' },
  { country: 'Canada', code: 'CA', dial: '+1' },
  { country: 'Australia', code: 'AU', dial: '+61' },
  { country: 'United Arab Emirates', code: 'AE', dial: '+971' }
];

const AddressModal = ({
  isAddressModalOpen,
  addressHandleCancel,
  setSelectedAddress,
  refreshAddresses,
  fetchAddresses,
  AllAddress,
  editingAddress = null,
  onAddressSaved,
}) => {
  const [form] = Form.useForm();
  const [selectedOption, setSelectedOption] = useState('Home');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_DIAL_CODES[0]);
  const [loading, setLoading] = useState(false);

  const profileData = JSON.parse(localStorage.getItem('profile') || 'null') || {};
  const userId = profileData?.id || profileData?.user_id;
  const isEditMode = Boolean(editingAddress);

  useEffect(() => {
    if (!isAddressModalOpen) return;

    if (isEditMode) {
      const nextCountryCode = editingAddress?.country_code || editingAddress?.countryCode || 'IN';
      const match = COUNTRY_DIAL_CODES.find((country) => country.code === nextCountryCode) || COUNTRY_DIAL_CODES[0];
      setSelectedCountry(match);
      setSelectedOption(editingAddress?.address_type || editingAddress?.addressType || 'Home');
       form.setFieldsValue({
            fullName: editingAddress?.full_name || editingAddress?.fullName || '',
            phone: editingAddress?.phone || '',
            alternativePhone: editingAddress?.alternative_phone || editingAddress?.alternativePhone || '',
            addressLine1: editingAddress?.address_line1 || editingAddress?.addressLine1 || editingAddress?.address || '',
            locality: editingAddress?.locality || '',
            city: editingAddress?.city || '',
            state: editingAddress?.state || '',
            postalCode: editingAddress?.postal_code || editingAddress?.postalCode || '',
            isDefault: editingAddress?.is_default || editingAddress?.isDefault || false,
        });
    } else {
      setSelectedOption('Home');
      setSelectedCountry(COUNTRY_DIAL_CODES[0]);
      form.resetFields();
    }
  }, [isAddressModalOpen, editingAddress, form, isEditMode]);

  const addressTypes = [
    { key: 'Home', icon: <RiHome2Line />, label: 'Home' },
    { key: 'Office', icon: <PiBagSimpleBold />, label: 'Office' },
    { key: 'Friend & Family', icon: <IoLocationOutline />, label: 'Friend & Family' },
    { key: 'Other', icon: <IoLocationOutline />, label: 'Other' }
  ];

  const handleCountryChange = (value) => {
    const found = COUNTRY_DIAL_CODES.find((c) => c.code === value);
    if (found) setSelectedCountry(found);
  };

  const handleFinish = async (values) => {
    if (!userId) {
      message.error('User not logged in. Please login first.');
      return;
    }

    setLoading(true);
    try {
      const payload = buildAddressPayload({ userId, values, selectedCountry, selectedOption });
      const endpoint = `${API_BASE_URL}addresses`;
      let response;

      if (isEditMode) {
        const addressId = editingAddress?.id || editingAddress?.address_id;
        if (!addressId) {
          throw new Error('Address id is missing for edit request.');
        }
        response = await axios.put(`${endpoint}/${addressId}`, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        response = await axios.post(`${endpoint}?userId=${userId}`, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (response.data?.success !== false) {
        const responseData = response.data;
        const resultData = responseData?.data || responseData?.address || responseData;
        const createdAddressId = getAddressId(resultData) ?? getAddressId(responseData);

        message.success(isEditMode ? 'Address updated successfully!' : 'Address added successfully!');
        form.resetFields();
        addressHandleCancel();

        const reloadAddresses = refreshAddresses || fetchAddresses || AllAddress;
        const refreshedAddresses = reloadAddresses ? await reloadAddresses(false) : [];
        const addressList = Array.isArray(refreshedAddresses) ? refreshedAddresses : [];
        const addressToSelect = addressList.find((address) =>
          String(address.id || address.address_id) === String(createdAddressId)
        ) || (resultData?.full_name || resultData?.fullName ? resultData : null);

        if (setSelectedAddress && addressToSelect) setSelectedAddress(addressToSelect);
        if (onAddressSaved) await onAddressSaved();
      } else {
        throw new Error(response.data?.message || (isEditMode ? 'Failed to update address' : 'Failed to create address'));
      }
    } catch (error) {
      console.error('API Error:', error);
      message.error(error.response?.data?.message || error.message || 'Error submitting address');
    } finally {
      setLoading(false);
    }
  };

  const prefixSelector = (
    <Select
      value={selectedCountry.code}
      onChange={handleCountryChange}
      style={{ width: 100 }}
    >
      {COUNTRY_DIAL_CODES.map((item) => (
        <Option key={item.code} value={item.code}>
          {item.dial} ({item.code})
        </Option>
      ))}
    </Select>
  );

  return (
    <Modal
      title={isEditMode ? 'Edit Address' : 'Add New Address'}
      open={isAddressModalOpen}
      onCancel={addressHandleCancel}
      width={720}
      footer={null}
      destroyOnClose
    >
      <div className="profile-modal-main-con">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{ countryCode: 'IN' }}
        >
          {/* Full Name & Phone */}
          <div className="row">
            <div className="col-md-6">
              <Form.Item
                name="fullName"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter your full name' }]}
              >
                <Input placeholder="Enter Full Name" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[
                  { required: true, message: 'Please enter phone number' },
                  { pattern: /^[0-9]{7,12}$/, message: 'Enter a valid phone number' }
                ]}
              >
                <Input addonBefore={prefixSelector} placeholder="9876543210" />
              </Form.Item>
            </div>
          </div>

          {/* Address Line 1 & Locality */}
          <Form.Item
            name="addressLine1"
            label="Address (House No, Flat, Street)"
            rules={[{ required: true, message: 'Please enter address' }]}
          >
            <Input placeholder="Flat 402, Building A, Sunshine Heights" />
          </Form.Item>

          <div className="row">
            <div className="col-md-6">
              <Form.Item name="locality" label="Locality / Landmark (Optional)">
                <Input placeholder="Near Central Park" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item name="alternativePhone" label="Alternative Phone (Optional)">
                <Input addonBefore={selectedCountry.dial} placeholder="Alternative Number" />
              </Form.Item>
            </div>
          </div>

          {/* City, State, Postal Code */}
          <div className="row">
            <div className="col-md-4">
              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: 'Please enter city' }]}
              >
                <Input placeholder="Mumbai" />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item
                name="state"
                label="State"
                rules={[{ required: true, message: 'Please enter state' }]}
              >
                <Input placeholder="Maharashtra" />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item
                name="postalCode"
                label="Pincode / Postal Code"
                rules={[
                  { required: true, message: 'Please enter postal code' },
                  { pattern: /^[0-9]{5,8}$/, message: 'Enter a valid pincode' }
                ]}
              >
                <Input placeholder="400001" maxLength={8} />
              </Form.Item>
            </div>
          </div>

          {/* Save As Selection */}
          <div className="address-model-order-types-options-main-con">
            <h4 style={{ marginBottom: '10px', fontWeight: '500' }}>Save Address As</h4>
            <div className="address-model-order-types-option-row" style={{ display: 'flex', gap: '12px' }}>
              {addressTypes.map((type) => {
                const isSelected = selectedOption === type.key;
                return (
                  <div
                    key={type.key}
                    onClick={() => setSelectedOption(type.key)}
                    style={{
                      border: isSelected ? '2px solid #1677ff' : '1px solid #d9d9d9',
                      backgroundColor: isSelected ? '#e6f4ff' : '#fff',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {type.icon}
                    <span style={{ fontWeight: isSelected ? '600' : 'normal' }}>{type.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Submit Button */}
          <div className="address-model-btn" style={{ marginTop: '24px', textAlign: 'right' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                backgroundColor: '#1677ff',
                color: '#fff',
                border: 'none',
                padding: '10px 24px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {loading ? 'Saving...' : 'Save Address'}
            </button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default AddressModal;